import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { authGuard } from './guards/auth.guard'
import { isPublicRegistrationEnabled } from '@/utils/public-registration-switch'

/**
 * #2077 — public self-service company signup ships CLOSED.
 *
 * The SPA half of the same switch the BFF reads as `PUBLIC_REGISTRATION_ENABLED`
 * (`apps/web/src/server/index.ts`). Build-time, per environment, exactly like every other
 * client flag in this app (`VITE_SENTRY_ENABLED`, `VITE_ENABLE_MOCK_SERVICES`): a strict
 * `=== 'true'` comparison, so absent, empty, 'TRUE', '1', 'yes' and every other value mean
 * OFF. The default is the control, not the configuration.
 *
 * OFF means the two route records are NOT REGISTERED — deliberately, rather than a route that
 * renders a "coming soon" page. An unregistered path is indistinguishable from a path this app
 * has never had: `/register` behaves exactly as `/not-a-real-page` does, which for a signed-out
 * visitor is the auth guard's redirect to `/login`. A placeholder page would instead advertise
 * that the feature exists and is coming — the precise disclosure this story removed.
 *
 * Reversible: set the build arg to 'true' and both records come back unchanged. Wired through
 * `infra/docker/web.Dockerfile` and the three deploy workflows.
 * Proven both ways by `tests/unit/router/public-registration-routes.gate.test.ts`.
 *
 * The same switch also gates US #1806's organisation-management route changes (#2568): see
 * `organisationManagementRoutes` and `AdminOrganisationDetail` below, proven both ways by
 * `tests/unit/router/organisation-management-routes.gate.test.ts`.
 */
const publicRegistrationEnabled = isPublicRegistrationEnabled()

/**
 * The self-service signup routes — the submission page and the emailed verification page.
 * Spread into `routes` below only while the switch above is on.
 */
const publicRegistrationRoutes: RouteRecordRaw[] = [
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/pages/public/RegisterPage.vue'),
    meta: {
      requiresAuth: false,
      title: 'Register Your Company',
    },
  },
  {
    path: '/register/verify/:token',
    name: 'RegisterVerify',
    component: () => import('@/pages/public/RegisterVerifyPage.vue'),
    meta: {
      requiresAuth: false,
      title: 'Verify Registration',
    },
  },
]

/**
 * US #1806's organisation-management routes that exist only while the switch is on (#2568).
 * Spread into `routes` below exactly as `publicRegistrationRoutes` is: off, the path is one this
 * router has never heard of.
 */
const organisationManagementRoutes: RouteRecordRaw[] = [
  {
    // #1809 — where the auth guard sends a member whose organisation may not operate. Only the
    // BFF's access gate, which rides the BFF half of this switch, ever sends anyone here.
    // ⚠️ `requiresAuth: false` is load-bearing: the guard redirects every route here while the
    // organisation is unavailable, so this one must be let through at the guard's top or it would
    // redirect to itself forever. Pinned by OrganisationUnavailablePage.test.ts.
    path: '/organisation-unavailable',
    name: 'OrganisationUnavailable',
    component: () => import('@/pages/OrganisationUnavailablePage.vue'),
    meta: { requiresAuth: false },
  },
  {
    // #1848 — where the auth guard sends a member who holds no usable seat. `requiresAuth: false`
    // is load-bearing for the same reason as above: the guard redirects every route here.
    path: '/no-seat',
    name: 'NoSeat',
    component: () => import('@/pages/NoSeatPage.vue'),
    meta: { requiresAuth: false },
  },
]

/**
 * Seat purchase routes (Task #1831, US #2570) — present only while the switch is on, spread into
 * `routes` below exactly as `organisationManagementRoutes` is: off, these paths are ones this
 * router has never heard of. Organisation-admin only, as the BFF routes behind them are
 * (`platform_admin` is NOT admitted: platform admins are org-less by policy and the BFF refuses
 * them, so the SPA would only lead there to an error). Proven both ways by `tests/unit/router/seat-billing-routes.gate.test.ts`.
 *
 * `/billing/fake-checkout` is the local stand-in for Stripe's hosted page. It is registered with
 * the others, but its endpoints exist only for the fake billing provider, and only in local, dev and test, so
 * anywhere else it can only say it is unavailable.
 */
const seatBillingRoutes: RouteRecordRaw[] = [
  {
    path: '/billing/seats',
    name: 'BuySeats',
    component: () => import('@/pages/billing/BuySeatsPage.vue'),
    meta: { requiresAuth: true, roles: ['organisation-admin'], title: 'Buy seats' },
  },
  {
    path: '/billing/seat-assignments',
    name: 'SeatAssignments',
    component: () => import('@/pages/billing/SeatsPage.vue'),
    meta: { requiresAuth: true, roles: ['organisation-admin'], title: 'Seats' },
  },
  {
    path: '/billing/subscription',
    name: 'SubscriptionStatus',
    component: () => import('@/pages/billing/SubscriptionStatusPage.vue'),
    meta: { requiresAuth: true, roles: ['organisation-admin'], title: 'Subscription' },
  },
  {
    path: '/billing/success',
    name: 'PurchaseSuccess',
    component: () => import('@/pages/billing/PurchaseSuccessPage.vue'),
    meta: { requiresAuth: true, roles: ['organisation-admin'], title: 'Purchase received' },
  },
  {
    path: '/billing/cancelled',
    name: 'PurchaseCancelled',
    component: () => import('@/pages/billing/PurchaseCancelledPage.vue'),
    meta: { requiresAuth: true, roles: ['organisation-admin'], title: 'Checkout cancelled' },
  },
  {
    path: '/billing/fake-checkout',
    name: 'FakeCheckout',
    component: () => import('@/pages/billing/FakeCheckoutPage.vue'),
    meta: { requiresAuth: true, roles: ['organisation-admin'], title: 'Test checkout' },
  },
  {
    // Task #1835: the local stand-in for the provider's payment-method page (fake provider only).
    path: '/billing/fake-payment-method',
    name: 'FakePaymentMethod',
    component: () => import('@/pages/billing/FakePaymentMethodPage.vue'),
    meta: { requiresAuth: true, roles: ['organisation-admin'], title: 'Test payment method' },
  },
]

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/pages/HomePage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reports',
    name: 'Reports',
    component: () => import('@/pages/ReportsPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/callback',
    name: 'Callback',
    component: () => import('@/pages/CallbackPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/forbidden',
    name: 'Forbidden',
    component: () => import('@/pages/ForbiddenPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/sites',
    name: 'Sites',
    component: () => import('@/pages/SitesPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/adhoc-reports',
    name: 'AdhocReports',
    component: () => import('@/pages/AdhocReportsPage.vue'),
    meta: { requiresAuth: true, title: 'Adhoc Reports' },
  },
  {
    path: '/surveys/:id/reports',
    name: 'SurveyReports',
    component: () => import('@/pages/ReportsPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/surveys/:surveyId/bom-report',
    name: 'BomReport',
    component: () => import('@/pages/BomReportView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/surveys/:surveyId/appendices',
    name: 'AppendicesReport',
    component: () => import('@/pages/AppendicesView.vue'),
    meta: { requiresAuth: true },
  },
  {
    // Route for blank/adhoc proposals that have no survey association
    path: '/proposals/:reportId/appendices',
    name: 'BlankProposalAppendices',
    component: () => import('@/pages/AppendicesView.vue'),
    meta: { requiresAuth: true },
  },
  {
    // Read-only view of an approved proposal exactly as the client signed it (frozen snapshot)
    path: '/proposals/:reportId/approved/:proposalId',
    name: 'ApprovedProposal',
    component: () => import('@/pages/ApprovedProposalView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/surveys/:surveyId/cable-matrix',
    name: 'CableMatrixReport',
    component: () => import('@/pages/CableMatrixReportPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/users',
    name: 'AdminUserRoles',
    component: () => import('@/features/admin/UserRoleManagementView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'platform_admin'],
      title: 'User Role Management',
    },
  },
  {
    path: '/admin/notification-subscriptions',
    name: 'NotificationSubscriptions',
    component: () => import('@/features/admin/NotificationSubscriptionsView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'platform_admin'],
      title: 'Notification Subscriptions',
    },
  },
  {
    path: '/admin/organisations',
    name: 'AdminOrganisations',
    component: () => import('@/pages/OrganisationsAdminPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['platform_admin'],
      title: 'Organisations',
    },
  },
  {
    path: '/admin/organisations/:id',
    name: 'AdminOrganisationDetail',
    component: () => import('@/pages/OrganisationDetailPage.vue'),
    meta: {
      requiresAuth: true,
      // While the switch is on, an org admin administers their OWN organisation here (#2200) —
      // the BFF was always written to admit them (`hasAdminRole` on `GET/PATCH /:id`, #554).
      // While it is off, the page is platform_admin-only, exactly as before #2200 (#2568): the
      // record stays registered, because platform admins use it today. The LIST route above
      // stays platform_admin-only either way: it enumerates every tenant. Scoping is the BFF's
      // job, not this list's — another organisation's id answers 404 for an org admin.
      roles: publicRegistrationEnabled
        ? ['platform_admin', 'organisation-admin']
        : ['platform_admin'],
      title: 'Organisation Detail',
    },
  },
  {
    path: '/admin/taxonomy',
    name: 'AdminTaxonomy',
    component: () => import('@/pages/TaxonomyAdminPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['platform_admin'],
      title: 'Taxonomy',
    },
  },
  {
    path: '/admin/hardware-vocabulary',
    name: 'AdminHardwareVocabulary',
    component: () => import('@/pages/HardwareVocabularyPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['manage-hardware', 'platform_admin'],
      title: 'Hardware Vocabulary',
    },
  },
  {
    // Consolidated role-aware Billing & Usage page (US #1035 / Task #1144) — replaces the six
    // routes below, which now just redirect here with the matching `?tab=`. `roles` is the
    // UNION of every tab's own role gate (see BillingPage.vue) so the guard admits anyone who
    // can see at least one tab; each tab's `visible` computed re-derives the exact same gate
    // its former standalone route had, so no access is widened by consolidating the routes.
    path: '/billing',
    name: 'Billing',
    component: () => import('@/pages/BillingPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['finance', 'organisation-admin', 'platform_finance', 'platform_admin'],
      title: 'Billing & Usage',
    },
  },
  {
    // Redirects (Task #1144) — kept so existing bookmarks/links never 404. The auth guard
    // evaluates the FINAL resolved route (`/billing`, above), not this record's meta.
    path: '/admin/ai-costs',
    redirect: () => ({ path: '/billing', query: { tab: 'usage' } }),
  },
  {
    path: '/admin/billing',
    redirect: () => ({ path: '/billing', query: { tab: 'rates-fx' } }),
  },
  {
    path: '/admin/invoices',
    redirect: () => ({ path: '/billing', query: { tab: 'all-invoices' } }),
  },
  {
    path: '/admin/ai-spend-caps',
    redirect: () => ({ path: '/billing', query: { tab: 'caps' } }),
  },
  {
    path: '/billing/invoices',
    redirect: () => ({ path: '/billing', query: { tab: 'my-invoices' } }),
  },
  {
    path: '/billing/details',
    redirect: () => ({ path: '/billing', query: { tab: 'billing-details' } }),
  },
  {
    path: '/admin/brand-standards',
    name: 'AdminBrandStandards',
    component: () => import('@/pages/BrandStandardsSettingsPage.vue'),
    meta: {
      requiresAuth: true,
      // organisation-admin included so the UI route matches the backend requireAnyRole
      // admin bypass (brand standards are org-scoped data) — #909.
      roles: ['manage-brand-standards', 'organisation-admin', 'platform_admin'],
      title: 'Brand Standards',
    },
  },
  {
    path: '/documents',
    name: 'DocumentsManagement',
    component: () => import('@/pages/DocumentsManagementPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'appendix-report-signoff', 'documents-manager'],
      title: 'Documents Management',
    },
  },
  {
    path: '/reports/:reportId/documents',
    name: 'ProposalDocuments',
    component: () => import('@/pages/ProposalDocumentsAdminPage.vue'),
    meta: {
      requiresAuth: true,
      // Had no `roles` at all, so any authenticated user could reach it — and since #1367 this
      // page renders the same DocumentsList/DocumentsTable with inline edit/delete affordances.
      // Matches the sibling /documents route so read access is consistent between the two
      // (#1367 review). The mutation gate is useCanManageDocuments(), mirroring the endpoint.
      roles: ['organisation-admin', 'appendix-report-signoff', 'documents-manager'],
      title: 'Proposal Documents',
    },
  },
  {
    path: '/admin/proposal-settings',
    name: 'ProposalSettings',
    component: () => import('@/pages/ProposalSettingsAdminPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'platform_admin'],
      title: 'Proposal Settings',
    },
  },
  {
    path: '/admin/ai-settings',
    name: 'AiModelSettings',
    component: () => import('@/pages/AiModelSettingsPage.vue'),
    meta: {
      // #820/#826: ai_model_configs is a GLOBAL table shared by all tenants, so AI Settings
      // is platform-admin only — an org admin must not read/repoint every tenant's models.
      requiresAuth: true,
      roles: ['platform_admin'],
      title: 'AI Settings',
    },
  },
  {
    path: '/builder',
    name: 'SurveyBuilder',
    component: () => import('@/pages/SurveyBuilderAdminPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'survey-builder', 'survey-admin'],
      title: 'Survey Builder',
    },
  },
  {
    path: '/builder/:id',
    name: 'SurveyBuilderDetail',
    component: () => import('@/pages/SurveyBuilderDetailPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'survey-builder', 'survey-admin'],
      title: 'Survey Detail',
    },
  },
  {
    path: '/builder/:id/hardware',
    name: 'SurveyHardwareManagement',
    component: () => import('@/pages/HardwareManagementPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'survey-builder', 'survey-admin'],
      title: 'Manage Hardware Categories',
    },
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('@/pages/SettingsPage.vue'),
    meta: {
      requiresAuth: true,
      roles: [
        'platform_admin',
        'organisation-admin',
        'appendix-report-signoff',
        'documents-manager',
        'survey-builder',
        'survey-admin',
        'manage-hardware',
        'manage-brand-standards',
      ],
      title: 'Settings',
    },
  },
  {
    path: '/settings/manage-surveys',
    name: 'ManageSurveys',
    component: () => import('@/pages/ManageSurveysPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'survey-admin'],
      title: 'Manage Surveys',
    },
  },
  {
    path: '/settings/manage-devices',
    name: 'ManageDevices',
    component: () => import('@/pages/ManageDevicesPage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['organisation-admin', 'survey-admin', 'survey-builder'],
      title: 'Manage My Devices',
    },
  },
  {
    path: '/public/proposals/:token',
    name: 'PublicProposal',
    component: () => import('@/pages/public/PublicProposalView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Proposal Review',
    },
  },
  // #2077 — present only while VITE_PUBLIC_REGISTRATION_ENABLED === 'true'. Off, this spreads
  // to nothing and `/register` is a path this router has never heard of.
  ...(publicRegistrationEnabled ? publicRegistrationRoutes : []),
  // #2568 — same switch, same shape: off, `/organisation-unavailable` is a path this router has
  // never heard of.
  ...(publicRegistrationEnabled ? organisationManagementRoutes : []),
  // Task #1831 — same switch, same shape: off, `/billing/seats` and its four siblings are paths
  // this router has never heard of.
  ...(publicRegistrationEnabled ? seatBillingRoutes : []),
  {
    path: '/invite/:token',
    name: 'InviteAccept',
    component: () => import('@/pages/InviteAcceptPage.vue'),
    meta: {
      requiresAuth: false,
      title: 'Accept Invitation',
    },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// Note: Auth guard is registered in main.ts after Pinia is initialized
// to avoid "getActivePinia()" errors

export default router

export { authGuard }
